import { AxiosError } from "axios";

import { getAccessToken, getCustomerId } from "@/helpers/auth-helpers";
import { getPartnerToken } from "./get-partner-token";
import { gupshupIntegrationService } from "@/api/customer-platform/whatsapp/gupshup-integration";
import { ICustomerPlatformInput } from "@/interfaces/customer-platform.interfaces";
import { apiT2B } from "@/lib/axios";

export async function configureGupshupSubscription(
  appId: string,
  callbackUrl: string,
  appDetails: {
    phoneNumber: string;
    appName: string;
  },
  platformId: number
): Promise<void> {
  try {
    const partnerToken = await getPartnerToken();
    const accessToken = getAccessToken();
    const customerId = getCustomerId();

    if (!accessToken || !customerId) {
      throw new Error("Missing authentication data");
    }

    const modes = [
      "ALL",
      "MESSAGE",
      "READ",
      "DELIVERED",
      "SENT",
      "DELETED",
      "FAILED",
      "ENQUEUED",
      "FLOWS_MESSAGE",
      "PAYMENTS",
      "TEMPLATE",
      "ACCOUNT",
      "BILLING",
      "OTHERS",
      "COEXISTENCE",
    ];

    let subscriptionConfigured = false;
    try {
      await gupshupIntegrationService.configureSubscription(
        appId,
        callbackUrl,
        modes,
        "V3 Subscription",
        "3",
        partnerToken
      );
      subscriptionConfigured = true;
    } catch (subscriptionError) {
      throw subscriptionError;
    }

    const appToken = await gupshupIntegrationService.getAppToken(appId, partnerToken);

    const gupshupDisplayName = await gupshupIntegrationService.getWhatsAppProfileDisplayName(
      appId,
      appToken
    );

    const customerPlatformData: ICustomerPlatformInput = {
      platformId,
      customerExternalId: appId, 
      platformProfileId: appDetails.phoneNumber, 
      displayNumber: appDetails.phoneNumber, 
      displayName: gupshupDisplayName || appDetails.appName,
      accessToken: appToken, 
      Active: true, 
      configuration: JSON.stringify({
        partnerToken: partnerToken, 
        subscriptionUrl: callbackUrl,
        appName: appDetails.appName,
        subscriptionConfigured: subscriptionConfigured,
        phoneNumber: appDetails.phoneNumber,
      }),
    };

    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        CustomerId: customerId,
      },
    };

    await apiT2B.post("api/v1/customerplatform", customerPlatformData, config);
  } catch (error) {
    
    if (error instanceof AxiosError) {
      throw new Error(
        `Failed to configure subscription/callback: ${error.response?.data?.message || error.message}`
      );
    }
    
    throw error;
    
    if (error instanceof AxiosError) {
      
      if (error.response?.status === 401 || error.message === "UNAUTHORIZED") {
        const newPartnerToken = await getPartnerToken();
        
        const modes = [
          "ALL",
          "MESSAGE",
          "READ",
          "DELIVERED",
          "SENT",
          "DELETED",
          "FAILED",
          "ENQUEUED",
          "FLOWS_MESSAGE",
          "PAYMENTS",
          "TEMPLATE",
          "ACCOUNT",
          "BILLING",
          "OTHERS",
          "COEXISTENCE",
        ];
        await gupshupIntegrationService.configureSubscription(
          appId,
          callbackUrl,
          modes,
          "V3 Subscription",
          "3",
          newPartnerToken
        );
        
        const accessToken = getAccessToken();
        const customerId = getCustomerId();
        if (accessToken && customerId) {
          const appToken = await gupshupIntegrationService.getAppToken(appId, newPartnerToken);
          const gupshupDisplayName = await gupshupIntegrationService.getWhatsAppProfileDisplayName(
            appId,
            appToken
          );

          const customerPlatformData: ICustomerPlatformInput = {
            platformId,
            customerExternalId: appId,
            platformProfileId: appDetails.phoneNumber,
            displayNumber: appDetails.phoneNumber,
            displayName: gupshupDisplayName || appDetails.appName,
            accessToken: appToken,
            Active: true,
            configuration: JSON.stringify({
              partnerToken: newPartnerToken,
              subscriptionUrl: callbackUrl,
              appName: appDetails.appName,
            }),
          };
          await apiT2B.post("api/v1/customerplatform", customerPlatformData, {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              CustomerId: customerId,
            },
          });
        }
        return;
      }
      throw new Error(
        `Failed to configure subscription: ${error.response?.data?.message || error.message}`
      );
    }
    throw error;
  }
}
